import type { ReactNode } from 'react';
import { Suspense } from 'react';

import * as Sentry from '@sentry/react';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import loglevel from 'loglevel';
import type { FallbackProps } from 'react-error-boundary';
import {
  ErrorBoundary as ReactErrorBoundary,
  // eslint-disable-next-line import/no-unresolved
} from 'react-error-boundary';

import { Button } from './Button';
import { NotificationBox } from './NotificationBox';
import { Spinner } from './Spinner';

/**
 * Error boundary with react-query support.
 */
export function ErrorBoundary(props: {
  children: ReactNode;
  /** Enables analytics on retry button */
  componentLocation?: string;
  errorTitle?: ReactNode | ((error: unknown) => ReactNode);
  errorMessage?: ReactNode | ((error: unknown) => ReactNode);
  renderError?: (props: FallbackProps) => ReactNode;
  onReset?: () => void;
  onError?: (error: unknown) => void;
}) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ReactErrorBoundary
          onError={(error) => {
            if (error instanceof Error) {
              loglevel.error(`[Boundary/error]: ${error.message}`, error);
              Sentry.captureException(error);
            } else {
              loglevel.error(
                `[Boundary/error]: Caught an error that is not an instance of Error: ${JSON.stringify(
                  error
                )}`
              );
              Sentry.captureMessage(
                'ErrorBoundary caught an error that is not an instance of Error',
                { extra: { error } }
              );
            }

            props.onError?.(error);
          }}
          onReset={() => {
            props.onReset?.();
            reset();
          }}
          fallbackRender={({ error, resetErrorBoundary }) => {
            if (props.renderError) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              return props.renderError({ error, resetErrorBoundary });
            }

            const errorInstance = error instanceof Error ? error : undefined;

            const errorTitle =
              typeof props.errorTitle === 'function'
                ? props.errorTitle(error)
                : props.errorTitle;

            const errorMessage =
              typeof props.errorMessage === 'function'
                ? props.errorMessage(error)
                : props.errorMessage;

            return (
              <NotificationBox
                variant="error"
                title={errorTitle ?? 'Something went wrong'}
                error={errorInstance}
              >
                <span>
                  {errorMessage ||
                    'Please try again or contact support if the problem persists.'}
                </span>
                <Button
                  size="medium"
                  variant="secondary"
                  className="mt-16"
                  onClick={() => {
                    resetErrorBoundary();
                  }}
                  analytics={
                    props.componentLocation
                      ? {
                          action: 'retry error boundary',
                          location: props.componentLocation,
                          text: 'Retry',
                        }
                      : undefined
                  }
                >
                  Retry
                </Button>
              </NotificationBox>
            );
          }}
        >
          {props.children}
        </ReactErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}

/**
 * Suspense and Error boundary built into one component.
 */
export function Boundary(props: {
  children: ReactNode;
  /** Enables analytics on retry button */
  componentLocation?: string;
  loaderComponent?: ReactNode;
  errorTitle?: ReactNode | ((error: unknown) => ReactNode);
  errorMessage?: ReactNode | ((error: unknown) => ReactNode);
  renderError?: (props: FallbackProps) => ReactNode;
  onReset?: () => void;
  onError?: (error: unknown) => void;
}) {
  return (
    <ErrorBoundary
      componentLocation={props.componentLocation}
      renderError={props.renderError}
      errorTitle={props.errorTitle}
      errorMessage={props.errorMessage}
      onReset={props.onReset}
      onError={props.onError}
    >
      <Suspense fallback={props.loaderComponent ?? <Spinner />}>
        {props.children}
      </Suspense>
    </ErrorBoundary>
  );
}
